'use client';

import React from 'react';
import Link from 'next/link';
import round from 'lodash/round';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CusButton from '@/components/CusButton';
import CityOptions from '@/components/job-ranking/CityOptions';
import OpenJobArea from '@/components/job-ranking/OpenJobArea';
import JobOpportunityCard from '@/components/job-opportunity-panel/JobOpportunityCard';
import RecommendJob, { RecommendJobType } from '@/components/RecommendJob';
import { Option } from '@/context/profile-builder';
import {
  JOB_RANKING_PAGE_ROUTER_PATH,
  JOB_TITLE_PAGE_ROUTER_PATH,
} from '@/constants/router-path';
import {
  SalaryRankType,
  OpenJobRankType,
  SALARY_RANK,
  OPEN_JOB_RANK,
  EXIST_LOCATIONS,
  JobTitleRankType,
  getL1Data,
} from '@/components/job-ranking/common';
import BgImage from '@/assets/job-ranking/bg.png';
import FilterIconImage from '@/assets/job-ranking/filter-icon.png';

export const PageDataItem = React.memo(
  (props: {
    data: SalaryRankType | OpenJobRankType;
    index: number;
    city: string;
    className?: string;
    isPost?: boolean;
    handleClick?: () => void;
  }) => {
    const { index, city, className = '', isPost, handleClick, data } = props;
    const { job_title_id: jobTitleId, job_title_name: jobTitleName } = data;

    return (
      <ListItem
        key={jobTitleId}
        className={`!flex !items-center !pl-6 !py-7 !bg-blue-50 !bg-opacity-20 !relative after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:block after:h-[1px] after:w-[83%] after:bg-slate-100 ${className}`}
        sx={{
          paddingRight: isPost ? '55px' : '85px',
        }}
        onClick={handleClick}
      >
        <i className="not-italic w-8 h-8 leading-8 rounded-full bg-blue-100 text-web-blue-500 text-sm text-center font-semibold mr-3.5">
          {index + 1}
        </i>
        <Link
          className="underline hover:text-web-blue-500"
          href={`${
            city ? `/${city}/` : JOB_TITLE_PAGE_ROUTER_PATH
          }${jobTitleId}/`}
          onClick={(e) => e.stopPropagation()}
        >
          {jobTitleName}
        </Link>
        <div className="ml-auto font-bold flex items-center">
          <span>
            {isPost
              ? 'current_count' in data && data.current_count
              : 'avg' in data && `¥${round(data.avg, -2)}`}
          </span>
          {isPost && 'open_job_by_month' in data && (
            <OpenJobArea areaData={data['open_job_by_month']} />
          )}
        </div>
      </ListItem>
    );
  },
);

const ListOptions = React.memo(
  (props: {
    label: string;
    options: SalaryRankType[] | OpenJobRankType[];
    city: string;
    isPost?: boolean;
    checkMoreHref?: string;
    recommendJobData?: RecommendJobType;
    handleClick?: (index: number) => void;
    itemClass?: string;
    selectedSalaryIndex?: number;
  }) => {
    const {
      label,
      options,
      isPost,
      checkMoreHref,
      recommendJobData,
      handleClick,
      itemClass = '',
      selectedSalaryIndex,
      city,
    } = props;
    if (!options.length) {
      return null;
    }

    return (
      <div className="mb-20">
        <h2 className="text-3.25xl leading-14 mb-6 font-semibold">{label}</h2>
        {selectedSalaryIndex !== undefined && recommendJobData && (
          <div className="rounded-lg mb-2 bg-blue-50/20">
            <RecommendJob
              {...recommendJobData}
              titleClass={`text-web-blue-500 pl-7 relative before:content-['${
                selectedSalaryIndex + 1
              }'] before:text-sm before:leading-6 before:text-center before:absolute before:left-0 before:top-0.5 before:block before:w-6 before:h-6 before:bg-blue-100 before:rounded-full`}
            />
            <div className="hidden before:content-['1'] before:content-['2'] before:content-['3'] before:content-['4'] before:content-['5'] before:content-['6'] before:content-['7'] before:content-['8'] before:content-['9'] before:content-['10']" />
          </div>
        )}
        <List className="!flex !flex-wrap !flex-col !gap-x-10 !max-h-[470px]">
          {options.slice(0, 10).map((item, index) => (
            <PageDataItem
              key={item.job_title_id}
              city={city}
              data={item}
              className={`!w-auto ${itemClass}`}
              index={index}
              isPost={isPost}
              handleClick={() => handleClick?.(index)}
            />
          ))}
        </List>
        {checkMoreHref && (
          <Link
            className="block text-web-blue-500 font-semibold mt-6 h-6 leading-6 flex items-center"
            href={checkMoreHref}
          >
            查看全部职位
            <KeyboardArrowRightIcon className="ml-2" />
          </Link>
        )}
      </div>
    );
  },
);

export const JobRankingHeader = () => {
  return (
    <div
      className="w-full bg-no-repeat bg-center bg-cover h-[185px]"
      style={{
        backgroundImage: `url(${BgImage.src})`,
      }}
    >
      <h1 className="pt-16 font-semibold leading-14 text-2.75xl px-3 max-w-[1176px] mx-auto">
        谈职职位排名
      </h1>
    </div>
  );
};

export const JobRankingPage = (props: {
  levelOneData: Option[];
  rankData: JobTitleRankType;
  filterId: string;
  city: string;
}) => {
  const { levelOneData, rankData, filterId, city } = props;
  const { hot_job_opportunities: opportunityJobs = [] } = rankData;
  const [showMore, setShowMore] = React.useState(false);
  const [selectedSalaryIndex, setSelectedSalaryIndex] = React.useState(0);

  const existLocationsData =
    rankData && EXIST_LOCATIONS in rankData ? rankData[EXIST_LOCATIONS] : [];
  const topSalaryData =
    rankData && SALARY_RANK in rankData ? rankData[SALARY_RANK] : [];
  const topPostData =
    rankData && OPEN_JOB_RANK in rankData ? rankData[OPEN_JOB_RANK] : [];

  const selectedSalaryData = topSalaryData[selectedSalaryIndex];
  const jobTTitleDetail = selectedSalaryData?.job_title_detail;
  const jobTitleId = selectedSalaryData?.job_title_id;
  const jobTitleName = selectedSalaryData?.job_title_name;

  const handleSalaryClick = (index: number) => {
    index < topSalaryData.length && setSelectedSalaryIndex(index);
  };

  const l1Data = React.useMemo(() => {
    return getL1Data(filterId, levelOneData);
  }, [filterId]);

  React.useEffect(() => {
    if (filterId) {
      const offsetTop = document.getElementById(filterId)?.offsetTop;
      if (offsetTop !== undefined && offsetTop > 0) {
        setShowMore(true);
      }
    }
  }, [filterId]);

  return (
    <div className="my-6 relative">
      {levelOneData.length > 0 && (
        <>
          <CusButton
            className="text-web-blue-500 text-base py-3 flex items-center absolute top-0 right-0 tablet:-right-20"
            onClick={() => setShowMore(!showMore)}
          >
            查看全部
            {showMore ? (
              <ArrowDropUpIcon className="ml-1" />
            ) : (
              <ArrowDropDownIcon className="ml-1" />
            )}
          </CusButton>
          <List
            className="!overflow-hidden !flex !p-0 !flex-wrap"
            sx={{ height: showMore ? 'auto' : 48 }}
          >
            {levelOneData.map((item) => (
              <li
                key={item.id}
                id={item.id}
                className={`h-full text-base font-semibold rounded hover:bg-blue-50 hover:text-web-blue-500 ${
                  filterId === item.id ? 'bg-blue-50 text-web-blue-500' : ''
                }`}
              >
                <Link
                  href={`${JOB_RANKING_PAGE_ROUTER_PATH}${item.id}/`}
                  className="block px-6 py-3 flex leading-6"
                >
                  <i
                    className="inline-block w-6 h-6 mr-2 bg-no-repeat bg-center bg-cover"
                    style={{
                      backgroundImage: `url(${FilterIconImage.src})`,
                    }}
                  />
                  {item.name}
                </Link>
              </li>
            ))}
          </List>
        </>
      )}
      <div className="mt-20 mb-6">
        <CityOptions
          options={existLocationsData}
          city={city}
          filterId={filterId}
        />
      </div>
      <ListOptions
        label={`${rankData.city_name}${l1Data?.name}薪资收入TOP10的职位`}
        options={topSalaryData}
        city={city}
        checkMoreHref={`${JOB_RANKING_PAGE_ROUTER_PATH}${
          city ? `${city}_${filterId}` : filterId
        }/topsalary/`}
        itemClass="!cursor-pointer hover:!bg-blue-50/80"
        handleClick={handleSalaryClick}
        recommendJobData={
          jobTTitleDetail
            ? {
                avg: jobTTitleDetail.salary_details.avg,
                min: jobTTitleDetail.salary_details.min,
                max: jobTTitleDetail.salary_details.max,
                currentCount: jobTTitleDetail.job_open_details.current_count,
                avgValueByExperienceDetails:
                  jobTTitleDetail.salary_details
                    .avg_value_by_experience_details,
                currentCountByExperience:
                  jobTTitleDetail.current_count_group_by_experience,
                id: jobTitleId,
                name: jobTitleName,
                jobTitleUrl: `${
                  city ? `/${city}/` : JOB_TITLE_PAGE_ROUTER_PATH
                }${jobTitleId}/`,
              }
            : undefined
        }
        selectedSalaryIndex={selectedSalaryIndex}
      />
      <ListOptions
        label={`${rankData.city_name}${l1Data?.name}职位发布趋势TOP10的职位`}
        options={topPostData}
        isPost={true}
        checkMoreHref={`${JOB_RANKING_PAGE_ROUTER_PATH}${
          city ? `${city}_${filterId}` : filterId
        }/toppost/`}
        city={city}
      />
      {opportunityJobs.length > 0 && (
        <div>
          <h2 className="tex text-3.25xl mb-6 font-semibold">热招职位</h2>
          <div className="grid gap-4 grid-rows-4 grid-cols-4">
            {opportunityJobs.slice(0, 16).map((item) => (
              <JobOpportunityCard key={item.id} data={item} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
